/**
 * The sign-in resend countdown, as arithmetic only.
 *
 * The gap between two code requests is a SERVER decision
 * (`auth.otpRequestMinIntervalSeconds`), handed to the client as the
 * `nextRequestAllowedAt` instant on every `otp/request` response. Nothing here
 * knows it is usually thirty seconds, and nothing here may learn it: an
 * operator raising the interval has to change what a fan sees without an app
 * release, which only works while the client reads the instant and does no
 * more than subtract.
 */

/**
 * Whole seconds left until `nextRequestAllowedAt`, floored at 0.
 *
 * Rounds UP, so a countdown started 30.0 seconds out reads "0:30" rather than
 * "0:29" on its first paint. An unparseable instant is 0 — a resend the fan
 * can take, because refusing one over a malformed timestamp strands them on a
 * screen with no way forward.
 */
export function secondsUntil(
  nextRequestAllowedAt: string,
  nowMs: number,
): number {
  const target = Date.parse(nextRequestAllowedAt);
  if (Number.isNaN(target)) return 0;
  return Math.max(0, Math.ceil((target - nowMs) / 1000));
}

/** "0:05", "0:30", "1:15" — minutes:seconds, seconds always two digits. */
export function formatResendCountdown(secondsRemaining: number): string {
  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export type typeResendControlState = {
  isDisabled: boolean;
  /** What the fan reads on the control, in either state. */
  label: string;
  secondsRemaining: number;
};

/**
 * Everything the resend control renders as, from the three inputs that decide
 * it. Pure, so both the web route and the mobile screen read one tested
 * function rather than two hand-kept copies of the same conditional.
 *
 * A missing `nextRequestAllowedAt` is "you may ask now", never a guessed gap:
 * a restored tab cannot know the instant, and inventing one would lock a fan
 * out of the one control that could rescue them. The address is deliberately
 * NOT an input — the control is offered either way, and the handler decides
 * whether a resend goes straight out or the fan is asked for the address
 * first. A control that is permanently present and permanently refusing is the
 * exact thing this screen exists to stop being.
 */
export function resolveResendControl({
  nextRequestAllowedAt,
  isRequestPending,
  nowMs,
}: {
  nextRequestAllowedAt: string | undefined;
  isRequestPending: boolean;
  nowMs: number;
}): typeResendControlState {
  const secondsRemaining =
    nextRequestAllowedAt === undefined || nextRequestAllowedAt === ""
      ? 0
      : secondsUntil(nextRequestAllowedAt, nowMs);
  const isReady = secondsRemaining <= 0;
  return {
    isDisabled: !isReady || isRequestPending,
    label: isReady
      ? "Send a new sign-in code"
      : `Resend in ${formatResendCountdown(secondsRemaining)}`,
    secondsRemaining,
  };
}
